/**
 * The Pick'em Results tab (ISSUE-1305), mounted by
 * `features/poolScreen/poolTypePanes.tsx`. Two views on one switch:
 *
 * - Round: one card per member with their points, record, round rank, the
 *   round winner mark, the tiebreaker guess and every pick with its result
 *   chip. A pick shows only once its own game locks; until then the server
 *   sends it as hidden and this tab says so. Round and member filters come
 *   from the server's own `filters`.
 * - Standings: the contest or season board, ranked by the server on points,
 *   then rounds won, then correct picks.
 */
import { SegmentedControl } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import {
  PICKEM_RESULTS_VIEW_LABEL,
  PICKEM_RESULTS_VIEW_ROUND,
  PICKEM_RESULTS_VIEW_STANDINGS,
} from "./pickemCopy";
import { PickemRoundResults } from "./PickemRoundResults";
import { PickemStandings } from "./PickemStandings";

type typeResultsView = "round" | "standings";

export const PickemResultsTab = ({
  poolId,
}: typePoolTypePaneProps): ReactNode => {
  const [view, setView] = useState<typeResultsView>("round");
  return (
    <div
      className="flex flex-col gap-4"
      data-testid="pickem-results-tab"
      data-pool-id={poolId}
    >
      <SegmentedControl
        label={PICKEM_RESULTS_VIEW_LABEL}
        options={[
          { value: "round", label: PICKEM_RESULTS_VIEW_ROUND },
          { value: "standings", label: PICKEM_RESULTS_VIEW_STANDINGS },
        ]}
        value={view}
        onValueChange={(next) => {
          setView(next === "standings" ? "standings" : "round");
        }}
        fullWidth
        testId="pickem-results-view"
      />
      {view === "round" ? (
        <PickemRoundResults poolId={poolId} />
      ) : (
        <PickemStandings poolId={poolId} />
      )}
    </div>
  );
};
